import { apiBase } from '@/config/runtime-config'
import { ApiError, type Problem } from './problem'
import { getAccessToken, setAccessToken, clearSession } from '@/shared/auth/tokenStore'

/**
 * Typed fetch wrapper.
 *
 * Access token lives in MEMORY only (never localStorage — XSS-exfiltratable).
 * The refresh token is an HttpOnly cookie the JS never sees, scoped to
 * /api/v1/auth, so it is not sent on ordinary API calls.
 */

let refreshInFlight: Promise<string | null> | null = null

/**
 * SINGLE-FLIGHT refresh. Mandatory, not an optimisation.
 *
 * The backend rotates refresh tokens and treats a replayed one as theft — it
 * revokes the entire token family. Two concurrent 401s each firing their own
 * refresh would log the user out of every device. Every caller awaits the same
 * promise.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) return null
      const data = (await res.json()) as { accessToken?: string }
      if (!data.accessToken) return null
      setAccessToken(data.accessToken)
      return data.accessToken
    } catch {
      return null
    } finally {
      // Release on the next tick so concurrent callers all observe this result.
      setTimeout(() => { refreshInFlight = null }, 0)
    }
  })()
  return refreshInFlight
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Skip the auth header + refresh dance (login, forgot-password, …). */
  anonymous?: boolean
  /** Internal: prevents an infinite refresh→retry loop. Retry ONCE, then stop. */
  _retried?: boolean
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, anonymous, _retried, headers, ...rest } = opts

  const h = new Headers(headers)
  h.set('Accept', 'application/json')
  if (body !== undefined) h.set('Content-Type', 'application/json')
  // Correlates the browser request with backend logs and traces.
  h.set('X-Request-Id', crypto.randomUUID())
  if (!anonymous) {
    const token = getAccessToken()
    if (token) h.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${apiBase}${path}`, {
    ...rest,
    headers: h,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 401 && !anonymous && !_retried) {
    const token = await refreshAccessToken()
    if (token) return request<T>(path, { ...opts, _retried: true })
    clearSession()
    const next = encodeURIComponent(window.location.pathname + window.location.search)
    if (!window.location.pathname.startsWith('/login')) {
      window.location.assign(`/login?next=${next}`)
    }
    throw new ApiError(401, { title: 'Session expired' })
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const payload = text ? JSON.parse(text) : null

  if (!res.ok) throw new ApiError(res.status, (payload ?? {}) as Problem)
  return payload as T
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
}

/**
 * Fetch a non-JSON body and hand it to the browser as a file download.
 *
 * `request` above parses every response as JSON, which is right for the rest of
 * the API and wrong for `GET /geography/import/template`, which returns
 * `text/csv`. This repeats the auth and single-flight-refresh dance rather than
 * generalising `request`, because the two differ in what they do with the body
 * and merging them would put a `responseType` branch in the hot path of every
 * call in the app.
 *
 * `filename` falls back to the server's `Content-Disposition` when omitted —
 * the backend already sends a good one.
 */
export async function downloadFile(
  path: string, filename?: string, _retried = false,
): Promise<void> {
  const h = new Headers({ Accept: '*/*', 'X-Request-Id': crypto.randomUUID() })
  const token = getAccessToken()
  if (token) h.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${apiBase}${path}`, { headers: h, credentials: 'include' })

  if (res.status === 401 && !_retried) {
    const next = await refreshAccessToken()
    if (next) return downloadFile(path, filename, true)
  }
  if (!res.ok) {
    // A failed download still returns a ProblemDetail, so surface it properly.
    let problem: Problem = { title: `Download failed (${res.status})` }
    try { problem = (await res.json()) as Problem } catch { /* not JSON */ }
    throw new ApiError(res.status, problem)
  }

  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const served = /filename="?([^"';]+)"?/i.exec(disposition)?.[1]
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename ?? served ?? 'download'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Revoking immediately can cancel the download in Safari; one tick is enough.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

/** Liveness probe for the login page's status strip. Never throws. */
export async function pingApi(): Promise<'up' | 'down'> {
  try {
    const res = await fetch(`${apiBase.replace(/\/api\/v1$/, '')}/actuator/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(4000),
    })
    return res.ok ? 'up' : 'down'
  } catch {
    return 'down'
  }
}
